import tinymce from "tinymce";
import "tinymce/themes/silver"; // Standard-Thema
import "tinymce/icons/default"; // Standard-Symbole
import "tinymce/models/dom"; // DOM Model für TinyMCE 6+

// Plugins (falls benötigt)
import "tinymce/plugins/lists";
import "tinymce/plugins/link";
import "tinymce/plugins/code";
import "tinymce/plugins/table";
import "tinymce/plugins/visualblocks";

import "../../vvvebjs/libs/media/media.js";
import { connectClaudeSocket, disconnectClaudeSocket } from "../websockets/claudeSocket.js";

/**
 * Platzhalter zum Einfügen: Menü in der Toolbar und Autovervollständigung ab "{{". Beides greift
 * auf dieselbe Liste zu, die der Server liefert — ohne Liste bleibt der Editor unverändert.
 */
const platzhalterHilfenRegistrieren = (editor, platzhalter) => {
  const eintraege = (muster) =>
    platzhalter
      .filter(
        (p) =>
          !muster ||
          p.name.toLowerCase().includes(muster.toLowerCase()) ||
          p.beschreibung.toLowerCase().includes(muster.toLowerCase()),
      )
      .map((p) => ({ type: "autocompleteitem", value: p.schreibweise, text: p.beschreibung }));

  editor.ui.registry.addMenuButton("platzhalter", {
    text: "Platzhalter",
    tooltip: "Platzhalter an der Cursorposition einfügen",
    fetch: (callback) =>
      callback(
        platzhalter.map((p) => ({
          type: "menuitem",
          text: `${p.schreibweise} — ${p.beschreibung}`,
          onAction: () => editor.insertContent(p.schreibweise),
        })),
      ),
  });

  editor.ui.registry.addAutocompleter("platzhalter", {
    trigger: "{",
    minChars: 0,
    columns: 1,
    // Beim Tippen von "{{" ist die zweite Klammer schon Teil des Musters
    fetch: (muster) => Promise.resolve(eintraege(muster.replace(/^\{+/, ""))),
    onAction: (api, bereich, wert) => {
      editor.selection.setRng(bereich);
      editor.insertContent(wert);
      api.hide();
    },
  });
};

export const initTinyMCE = (selector, options = {}) => {
  const platzhalter = options.platzhalter || [];
  const werkzeugleiste =
    "undo redo | bold italic forecolor backcolor | " +
    (platzhalter.length > 0 ? " platzhalter " : "") +
    "mediaManager magicWand | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | blocks fontfamily fontsize | table visualblocks | removeformat code";

  const getStoredTheme = () => {
    try {
      if (typeof document !== "undefined" && document.documentElement) {
        const attr = document.documentElement.getAttribute("data-bs-theme");
        if (attr) return attr;
      }
      if (
        typeof localStorage !== "undefined" &&
        localStorage &&
        typeof localStorage.getItem === "function"
      ) {
        return localStorage.getItem("theme");
      }
    } catch {
      // ignore
    }
    return "dark";
  };

  const isDark = getStoredTheme() === "dark";

  const defaults = {
    selector: selector,
    base_url: "/js/tinymce",
    language: "de",
    skin: isDark ? "oxide-dark" : "oxide",
    content_css: isDark ? "dark" : "default",
    content_style: isDark
      ? "body { background-color: #2A2C31; color: #FFFFFF; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 14px; }"
      : "body { background-color: #FFFFFF; color: #1F2937; font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size: 14px; }",
    plugins: "lists link code table visualblocks",
    toolbar: werkzeugleiste,
    menubar: false,
    license_key: "gpl",
    branding: false,
    promotion: false,
    min_height: 400,
    setup: function (editor) {
      editor.on("init", function () {
        const doc = editor.getDoc();
        if (doc && doc.body) {
          const dark = getStoredTheme() === "dark";
          doc.body.style.backgroundColor = dark ? "#2A2C31" : "#FFFFFF";
          doc.body.style.color = dark ? "#FFFFFF" : "#1F2937";
        }
      });

      if (typeof window !== "undefined") {
        window.addEventListener("themeChanged", function (e) {
          const dark = e.detail?.theme === "dark";
          const doc = editor.getDoc();
          if (doc && doc.body) {
            doc.body.style.backgroundColor = dark ? "#2A2C31" : "#FFFFFF";
            doc.body.style.color = dark ? "#FFFFFF" : "#1F2937";
          }
        });
      }

      editor.on("change", function () {
        tinymce.triggerSave();
      });

      if (platzhalter.length > 0) {
        platzhalterHilfenRegistrieren(editor, platzhalter);
      }

      if (options.setup && typeof options.setup === "function") {
        options.setup(editor);
      }

      editor.ui.registry.addButton("mediaManager", {
        icon: "gallery",
        tooltip: "Medien einfügen",
        onAction: () => {
          if (typeof window.MediaModal !== "function") {
            cs.Swal.fire({
              icon: "error",
              title: "Fehler",
              text: "Der Medien-Manager konnte nicht geladen werden. Bitte laden Sie die Seite neu.",
            });
            return;
          }

          if (!window._surveyMediaModal) {
            window._surveyMediaModal = new window.MediaModal(true);
          }
          window._surveyMediaModal.open(null, function (src) {
            editor.insertContent(`<img src="${src}" alt="media" style="max-width: 100%;">`);
          });
        },
      });

      editor.ui.registry.addIcon("magicWand", '<i class="bi bi-stars"></i>');

      editor.ui.registry.addButton("magicWand", {
        icon: "magicWand",
        tooltip: "Text mit KI generieren",
        onAction: () => {
          const modalEl = document.getElementById("claudePromptModal");
          if (!modalEl) {
            cs?.Swal?.fire({
              icon: "error",
              title: "Fehler",
              text: "KI Modal nicht gefunden.",
            });
            return;
          }

          const bsModal = window.bootstrap.Modal.getOrCreateInstance(modalEl);
          const promptInput = document.getElementById("claudePromptInput");
          const generateBtn = document.getElementById("claudeGenerateBtn");
          const inputWrapper = document.getElementById("claudeInputWrapper");
          const loadingState = document.getElementById("claudeLoadingState");
          const loadingText = document.getElementById("claudeLoadingText");

          // Reset modal state
          promptInput.value = "";
          inputWrapper.classList.remove("d-none");
          loadingState.classList.add("d-none");
          generateBtn.disabled = false;

          bsModal.show();

          // Remove old listeners to avoid multiple triggers
          const newGenerateBtn = generateBtn.cloneNode(true);
          generateBtn.parentNode.replaceChild(newGenerateBtn, generateBtn);

          newGenerateBtn.addEventListener("click", () => {
            const prompt = promptInput.value.trim();
            if (!prompt) return;

            const taskId = crypto.randomUUID();

            // UI auf Laden setzen
            newGenerateBtn.disabled = true;
            inputWrapper.classList.add("d-none");
            loadingState.classList.remove("d-none");
            loadingText.textContent = "Bitte warten, Verbindung wird hergestellt...";

            // Socket verbinden
            connectClaudeSocket(
              taskId,
              (result) => {
                if (result.status === "GENERATION_STARTED") {
                  loadingText.textContent = "Text wird generiert...";
                } else if (result.status === "SUCCESS") {
                  bsModal.hide();

                  try {
                    let payloadStr = result.payload;
                    const jsonMatch = payloadStr.match(/```(?:json)?\s*([\s\S]*?)\s*```/);
                    if (jsonMatch && jsonMatch[1]) {
                      payloadStr = jsonMatch[1];
                    }

                    let data = {};
                    try {
                      // Zuerst versuchen wir Newlines in Strings zu fixen und normal zu parsen
                      let fixedStr = payloadStr.replace(
                        /"([^"\\]*(?:\\.[^"\\]*)*)"/g,
                        function (match, p1) {
                          return (
                            '"' +
                            p1.replace(/\n/g, "\\n").replace(/\r/g, "\\r").replace(/\t/g, "\\t") +
                            '"'
                          );
                        },
                      );
                      data = JSON.parse(fixedStr);
                    } catch (e) {
                      console.warn(
                        "Standard JSON.parse fehlgeschlagen, verwende Regex-Fallback",
                        e,
                      );
                      // Fallback: Regex Extraktion für kaputtes JSON (z.B. unescaped Quotes in HTML oder harte Umbrüche)
                      const extractField = (field) => {
                        const regex = new RegExp(
                          `"${field}"\\s*:\\s*"([\\s\\S]*?)"\\s*(?:,|\\}\\s*$)`,
                        );
                        const match = payloadStr.match(regex);
                        if (match && match[1]) {
                          return match[1]
                            .replace(/\\"/g, '"')
                            .replace(/\\\\/g, "\\")
                            .replace(/\\n/g, "\n");
                        }
                        return null;
                      };

                      data.subject = extractField("subject");
                      data.body_html = extractField("body_html");
                    }

                    if (data.subject) {
                      const subjectField = document.getElementById("subject");
                      if (subjectField) {
                        subjectField.value = data.subject;
                      }
                    }

                    if (data.body_html) {
                      editor.insertContent(data.body_html);
                    } else if (data.subject || Object.keys(data).length > 0) {
                      // Wenn wir *etwas* extrahieren konnten, aber kein body_html da ist, tun wir nichts weiter
                    } else {
                      // Komplettes Fallback
                      editor.insertContent(result.payload);
                    }
                  } catch (e) {
                    console.error("Kritischer Fehler bei der KI-Antwort Verarbeitung", e);
                    editor.insertContent(result.payload);
                  }

                  cs?.Swal?.fire({
                    icon: "success",
                    title: "Erfolg",
                    text: "Text erfolgreich generiert!",
                    toast: true,
                    position: "top-end",
                    showConfirmButton: false,
                    timer: 3000,
                  });
                }
              },
              (error) => {
                loadingText.textContent =
                  "Ein Fehler ist aufgetreten: " + (error.error || error.message || "Unbekannt");
                loadingState.classList.remove("text-primary");
                loadingState.classList.add("text-danger");

                setTimeout(() => {
                  bsModal.hide();
                  cs?.Swal?.fire({
                    icon: "error",
                    title: "Fehler",
                    text: "Generierung fehlgeschlagen.",
                  });
                }, 2000);
              },
            );

            // REST Call
            const csrfToken = document.querySelector('meta[name="_csrf"]')?.content;
            const csrfHeader = document.querySelector('meta[name="_csrf_header"]')?.content;

            const headers = { "Content-Type": "application/json" };
            if (csrfToken && csrfHeader) {
              headers[csrfHeader] = csrfToken;
            }

            fetch("/api/ai/generate/email", {
              method: "POST",
              headers: headers,
              body: JSON.stringify({
                taskId: taskId,
                prompt: prompt,
              }),
            })
              .then((res) => {
                if (!res.ok) {
                  throw new Error("API Request fehlgeschlagen");
                }
              })
              .catch((err) => {
                console.error(err);
                loadingText.textContent = "Ein Fehler ist aufgetreten: API Request fehlgeschlagen";
                setTimeout(() => bsModal.hide(), 2000);
              });
          });
        },
      });
    },
  };

  // platzhalter ist unsere eigene Angabe und darf nicht als unbekannte Option bei TinyMCE landen
  const { platzhalter: _platzhalter, ...tinymceOptions } = options;
  const mergedOptions = { ...defaults, ...tinymceOptions };

  // Ensure selector is passed correctly if it was in options
  if (!mergedOptions.selector) {
    mergedOptions.selector = selector;
  }

  return tinymce.init(mergedOptions);
};

export default tinymce;
